import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  AfterViewInit,
} from '@angular/core';

import { Chart, ChartConfiguration, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import moment from 'moment';
import { BaseChartDirective } from 'ng2-charts';

export interface NDVIDataPoint {
  date: string; // formato YYYY-MM-DD
  ndvi: number;
}

@Component({
  selector: 'app-ndvi-chart',
  standalone: true,
  imports: [BaseChartDirective],
  templateUrl: './ndvi-chart.component.html',
  styleUrls: ['./ndvi-chart.component.scss'],
})
export class NDVIChartComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() data: NDVIDataPoint[] = [];
  @Input() plotId?: number;
  @Input() plotName?: string;

  @Output() pointClick = new EventEmitter<NDVIDataPoint>();

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  public chartConfiguration: ChartConfiguration<'line'> = {
    type: 'line',
    data: {
      labels: [],
      datasets: [
        {
          label: 'NDVI',
          data: [],
          borderColor: '#2E8B57',
          backgroundColor: 'rgba(46, 139, 87, 0.1)',
          borderWidth: 2,
          pointBackgroundColor: '#2E8B57',
          pointBorderColor: '#2E8B57',
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          tension: 0.3,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: 'Gráfico de NDVI',
          font: {
            size: 16,
            weight: 'bold',
          },
          padding: 20,
        },
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (tooltipItems) => {
              if (tooltipItems.length > 0) {
                const dataIndex = tooltipItems[0].dataIndex;
                const originalDate = this.data[dataIndex]?.date;
                if (originalDate) {
                  return moment(originalDate).format('DD-MM-YYYY');
                }
              }
              return '';
            },
            label: (tooltipItem) => {
              return `NDVI: ${Number(tooltipItem.raw).toFixed(3)}`;
            },
          },
        },
        annotation: {
          annotations: {},
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Data',
            font: {
              size: 14,
              weight: 'bold',
            },
          },
          grid: {
            display: false,
            color: 'rgba(0, 0, 0, 0.1)',
          },
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 45,
            callback: (value, index) => {
              // Mostrar apenas labels nos dias 1 e 15 de cada mês
              const dataPoint = this.data[index];
              if (dataPoint) {
                const day = moment(dataPoint.date).date();
                if (day === 1 || day === 15) {
                  return (
                    (this.chartConfiguration.data.labels?.[index] as string) ||
                    ''
                  );
                }
              }
              return '';
            },
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'NDVI',
            font: {
              size: 14,
              weight: 'bold',
            },
          },
          min: 0,
          max: 1,
          grid: {
            display: false,
            color: 'rgba(0, 0, 0, 0.1)',
          },
          ticks: {
            stepSize: 0.2,
            callback: function (value) {
              return Number(value).toFixed(1);
            },
          },
        },
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
      },
      hover: {
        mode: 'nearest',
        intersect: false,
      },
      elements: {
        point: {
          hoverBackgroundColor: '#FF6B6B',
          hoverBorderColor: '#FF6B6B',
        },
      },
    },
  };

  constructor() {
    // Configurar locale do moment para português
    moment.locale('pt-br');
  }

  ngOnInit(): void {
    // Registrar os plugins do Chart.js
    Chart.register(...registerables, annotationPlugin);
    this.processData();
  }

  ngAfterViewInit(): void {
    this.updateChart();
  }

  ngOnDestroy(): void {
    if (this.chart?.chart) {
      this.chart.chart.destroy();
    }
  }

  private processData(): void {
    if (!this.data || this.data.length === 0) {
      return;
    }

    // Ordenar dados por data
    const sortedData = [...this.data].sort(
      (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
    );
    console.log(sortedData);
    // Processar datas para o formato quinzenal
    const processedData = sortedData.map((item) => ({
      ...item,
      processedDate: this.processDateToQuinzenal(item.date),
      formattedDate: moment(item.date).format('DD-MM-YYYY'),
    }));

    // Configurar dados do gráfico
    this.chartConfiguration.data.labels = processedData.map(
      (item) => item.formattedDate,
    );
    this.chartConfiguration.data.datasets[0].data = processedData.map(
      (item) => item.ndvi,
    );

    // Atualizar título se tiver nome do talhão
    if (this.plotName) {
      this.chartConfiguration.options!.plugins!.title!.text = `Gráfico de NDVI`;
    }
  }

  private processDateToQuinzenal(dateString: string): string {
    const date = moment(dateString);
    const day = date.date();

    // Ajustar para quinzenal (1º ou 15º dia do mês)
    if (day <= 15) {
      return date.date(1).format('DD-MM-YYYY');
    } else {
      return date.date(15).format('DD-MM-YYYY');
    }
  }

  private updateChart(): void {
    if (this.chart?.chart) {
      this.chart.chart.update('active');
    }
  }

  public updateData(newData: NDVIDataPoint[]): void {
    this.data = newData;
    this.processData();
    this.updateChart();
  }

  public getDateRange(): string {
    if (!this.data || this.data.length === 0) {
      return '';
    }

    const sortedData = [...this.data].sort(
      (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
    );

    const startDate = moment(sortedData[0].date).format('DD-MM-YYYY');
    const endDate = moment(sortedData[sortedData.length - 1].date).format(
      'DD-MM-YYYY',
    );

    return `${startDate} até ${endDate}`;
  }

  // Método para eventos de click no gráfico
  public onChartClick(event: any, activeElements: any[]): void {
    if (activeElements && activeElements.length > 0) {
      const pointIndex = activeElements[0].index;
      const dataPoint = this.data[pointIndex];

      if (dataPoint) {
        console.log('Ponto clicado:', {
          date: moment(dataPoint.date).format('DD-MM-YYYY'),
          ndvi: dataPoint.ndvi,
          index: pointIndex,
        });

        // Emitir evento com os dados do ponto clicado
        this.pointClick.emit(dataPoint);
      }
    }
  }
}
